import {
    CouponAlgorithm,
    CouponScope,
    LineItemTemplate,
    LineItemType,
    PurchaseRule,
    PurchaseRuleLink,
    TagId,
    TermsTemplateFee,
} from "types";
import { filterPurchaseRulesByProductId, getProductIdsFromPurchaseRules, getPurchaseRulesByTag } from "./PurchaseRule";
import {
    getAlgorithm,
    getAmount,
    getDemographicSelection,
    getEquipmentSelection,
    getIncludeCustomLineItems,
    getIncludesAddOns,
    getScope,
} from "./TermsTemplatePurchaseRule";

export const generateProductTermExecptions = (
    purchaseRules: PurchaseRule[] | null,
    termsTemplate?: PurchaseRuleLink,
    demographicTemplates?: LineItemTemplate[],
) => {
    if (!purchaseRules) {
        return [];
    }

    // Get all purchase rules with AFFILIATE_OVERRIDE tag
    purchaseRules = getPurchaseRulesByTag(TagId.AFFILIATE_OVERRIDE, purchaseRules);

    const discountPurchaseRules = getPurchaseRulesByTag(LineItemType.AFFILIATE_DISCOUNT, purchaseRules);

    if (discountPurchaseRules.every((purchaseRule) => !purchaseRule.filter) && termsTemplate) {
        // all the products are selected for this exception
        const discountPurchaseRule = getPurchaseRulesByTag(LineItemType.AFFILIATE_DISCOUNT, purchaseRules)[0];
        const commissionPurchaseRule = getPurchaseRulesByTag(LineItemType.AFFILIATE_COMMISSION, purchaseRules)[0];

        return discountPurchaseRule && commissionPurchaseRule
            ? [
                  {
                      id: `${discountPurchaseRule?.id}_${commissionPurchaseRule?.id}`,
                      products: { selected: [], all: true },
                      discount: getPurchaseRuleInputValues(discountPurchaseRule, termsTemplate, demographicTemplates),
                      commission: getPurchaseRuleInputValues(
                          commissionPurchaseRule,
                          termsTemplate,
                          demographicTemplates,
                      ),
                  },
              ]
            : [];
    }

    // get list of products from all Purchase rules, used discountPurchaseRule
    // as each Terms has same number of purchase rules for either discount or commission
    const selectedProductIds: string[] = getProductIdsFromPurchaseRules(discountPurchaseRules);
    const exceptions = [];

    for (const productId of selectedProductIds) {
        // filtered purchase rules of single product
        const productPurchaseRules = filterPurchaseRulesByProductId(purchaseRules, productId);
        // filtered discount purchase rules of single product
        const discountPurchaseRule = getPurchaseRulesByTag(LineItemType.AFFILIATE_DISCOUNT, productPurchaseRules)[0];
        // filtered commission purchase rules of single product
        const commissionPurchaseRule = getPurchaseRulesByTag(
            LineItemType.AFFILIATE_COMMISSION,
            productPurchaseRules,
        )[0];

        exceptions.push({
            productId,
            discount: discountPurchaseRule.id,
            commission: commissionPurchaseRule.id,
        });
    }

    const groupedExceptions: any = [];

    // Since we need to show 1 exception by combining discount purchase rule and commission purchase rule, we group them by product id.
    // loop through exsisting exceptions and group them based on the prodcut id.
    for (const exception of exceptions) {
        const existingGroup = groupedExceptions.find(
            (group: any) => group.discount === exception.discount && group.commission === exception.commission,
        );
        if (existingGroup) {
            existingGroup.productIds.push(exception.productId);
        } else {
            groupedExceptions.push({
                productIds: [exception.productId],
                discount: exception.discount, // discount purchase rule id
                commission: exception.commission, // commission purchase rule id
            });
        }
    }

    // Process grouped exceptions and extract values from purchase rules into Terms Template Fee format
    return groupedExceptions.map((groupedException: any) => {
        const discountPurchaseRule = purchaseRules?.find((rule) => rule.id === groupedException.discount);
        const commissionPurchaseRule = purchaseRules?.find((rule) => rule.id === groupedException.commission);
        return {
            id: `${discountPurchaseRule?.id}_${commissionPurchaseRule?.id}`,
            products: groupedException.productIds,
            discount: getPurchaseRuleInputValues(discountPurchaseRule!, termsTemplate, demographicTemplates),
            commission: getPurchaseRuleInputValues(commissionPurchaseRule!, termsTemplate, demographicTemplates),
        };
    });
};

export const defaultProductTermException = {
    products: [],
    discount: {
        amount: null,
        algorithm: CouponAlgorithm.ABSOLUTE,
        scope: CouponScope.PER_PERSON,
        demographics: { all: true, selected: [] },
        includeAddOns: false,
        includeCustomLineItems: false,
    },
    commission: {
        amount: null,
        algorithm: CouponAlgorithm.ABSOLUTE,
        scope: CouponScope.PER_PERSON,
        demographics: { all: true, selected: [] },
        includeAddOns: false,
        includeCustomLineItems: false,
    },
};

/**
 * Convert terms template purchase rule to form values
 */
export const getPurchaseRuleInputValues = (
    purchaseRule: PurchaseRule,
    termsTemplate?: PurchaseRuleLink,
    demographicTemplates?: LineItemTemplate[],
    equipmentTemplateIds?: ID[],
): TermsTemplateFee => {
    return {
        amount: termsTemplate ? getAmount(purchaseRule) : 0,
        algorithm: getAlgorithm(purchaseRule),
        scope: getScope(purchaseRule, new Set(equipmentTemplateIds ?? [])),
        demographics: getDemographicSelection(purchaseRule, demographicTemplates),
        equipments: getEquipmentSelection(purchaseRule, equipmentTemplateIds),
        includeAddOns: getIncludesAddOns(purchaseRule),
        includeCustomLineItems: getIncludeCustomLineItems(purchaseRule),
    };
};
